import React, { useState, useRef, useEffect, useMemo } from "react";
import { getFlagEmoji } from "../helpers/emoji";
import {
  ComboboxContainer,
  ComboboxInputWrapper,
  ComboboxInput,
  ComboboxSearchIcon,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxOptionFlag,
  ComboboxOptionText,
  ComboboxOptionCode,
  ComboboxNoResults,
} from "../styles";

const SEARCH_ICON = "🔍";
const NO_RESULTS_TEXT = "No se encontraron países";
const PLACEHOLDER_TEXT = "Buscar país...";

/**
 * Country combobox component with search functionality
 * @param {Object} props
 * @param {string} props.value - Selected country code (e.g., "MX")
 * @param {Function} props.onChange - Callback when country is selected
 * @param {Array} props.countries - Array of country objects
 */
export const CountryCombobox = ({ value, onChange, countries }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const filteredCountries = useMemo(() => {
    if (!searchTerm.trim()) {
      return countries;
    }

    const term = searchTerm.toLowerCase();

    return countries.filter(
      (country) =>
        country.country.toLowerCase().includes(term) ||
        country.nameCode.toLowerCase().includes(term) ||
        country.code.includes(term),
    );
  }, [countries, searchTerm]);

  const handleSelect = (country) => {
    onChange(country.nameCode);
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);

    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <ComboboxContainer ref={containerRef}>
      <ComboboxInputWrapper>
        <ComboboxSearchIcon>{SEARCH_ICON}</ComboboxSearchIcon>

        <ComboboxInput
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={PLACEHOLDER_TEXT}
          autoComplete="off"
        />
      </ComboboxInputWrapper>

      {isOpen && (
        <ComboboxDropdown>
          {filteredCountries.length > 0 ? (
            filteredCountries.map((country) => (
              <ComboboxOption
                key={country.nameCode}
                selected={country.nameCode === value}
                onClick={() => handleSelect(country)}
              >
                <ComboboxOptionFlag>
                  {getFlagEmoji(country.nameCode)}
                </ComboboxOptionFlag>

                <ComboboxOptionText>{country.country}</ComboboxOptionText>

                <ComboboxOptionCode>+{country.code}</ComboboxOptionCode>
              </ComboboxOption>
            ))
          ) : (
            <ComboboxNoResults>{NO_RESULTS_TEXT}</ComboboxNoResults>
          )}
        </ComboboxDropdown>
      )}
    </ComboboxContainer>
  );
};
